import React from 'react';
import type { MessageDescriptor } from 'react-intl';
import { useSelector } from 'react-redux';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import { Select } from '@swf/sports/components';
import type { SelectItem } from '@swf/sports/components/Select/Select';

import { useSportsMatchFilteringWidget } from '../model';
import type { TranslationTag } from '../model/types';

import {
  TXTCLI_Duration,
  TXTCLI_Total_Points,
  TXTWCL_Away_Team_Total_Points,
  TXTWCL_Both_Players_to_win_a_Set_XYes_ELL,
  TXTWCL_Both_Teams_to_Score,
  TXTWCL_Double_Chance,
  TXTWCL_Game_Lines,
  TXTWCL_Handicap,
  TXTWCL_Home_Team_Total_Points,
  TXTWCL_Market,
  TXTWCL_Match_Betting,
  TXTWCL_Match_Result,
  TXTWCL_Match_and_Both_Players_to_win__ELL,
  TXTWCL_Next_N0_Days,
  TXTWCL_Next_N0_Hours,
  TXTWCL_Over_Under_2X5_Goals,
  TXTWCL_Player_A_to_win_at_least_one_S_ELL,
  TXTWCL_To_Win_1st_Set
} from './Filters.messages.json';
import styles from './Filters.scss';

const marketTypeTranslationMap: Record<TranslationTag, MessageDescriptor> = {
  ISP_I18N_BOTH_TEAMS_TO_SCORE: TXTWCL_Both_Teams_to_Score,
  ISP_I18N_DOUBLE_CHANCE: TXTWCL_Double_Chance,
  'ISP_I18N_OVER_UNDER_2.5': TXTWCL_Over_Under_2X5_Goals,
  ISP_I18N_WIN_DRAW_WIN: TXTWCL_Match_Result,
  ISP_I18N_BOTH_PLAYERS_TO_WIN_A_SET: TXTWCL_Both_Players_to_win_a_Set_XYes_ELL,
  ISP_I18N_MATCH_AND_BOTH_PLAYERS_TO_WIN_A_SET: TXTWCL_Match_and_Both_Players_to_win__ELL,
  ISP_I18N_MATCH_BETTING: TXTWCL_Match_Betting,
  ISP_I18N_PLAYER_A_TO_WIN_AT_LEAST_1_SET: TXTWCL_Player_A_to_win_at_least_one_S_ELL,
  ISP_I18N_TO_WIN_1ST_SET: TXTWCL_To_Win_1st_Set,
  ISP_I18N_AWAY_TEAM_TOTAL_POINTS: TXTWCL_Away_Team_Total_Points,
  ISP_I18N_HANDICAP_BETTING: TXTWCL_Handicap,
  ISP_I18N_HOME_TEAM_TOTAL_POINTS: TXTWCL_Home_Team_Total_Points,
  ISP_I18N_TOTAL_POINTS_ODD_EVEN: TXTCLI_Total_Points,
  ISP_I18N_GAME_LINES: TXTWCL_Game_Lines
} as const;

const durationTranslationMap: Record<TranslationTag, MessageDescriptor> = {
  ISP_I18N_NEXT_N0_HOURS: TXTWCL_Next_N0_Hours,
  ISP_I18N_NEXT_N0_DAYS: TXTWCL_Next_N0_Days
} as const;

interface Props {
  onChangeHandler: (selectedItem: SelectItem<string>, param: string) => void;
}

const Filters: React.FC<Props> = ({ onChangeHandler }) => {
  const { formatMessage } = useRegulatoryIntl();
  const { widgetSelect } = useSportsMatchFilteringWidget();
  const firstLoadComplete = useSelector(widgetSelect.firstLoadComplete);
  const filterDisabled = useSelector(widgetSelect.showLoadingIndicator);
  const { durations, marketTypes } = useSelector(widgetSelect.filtersOptions);
  const filterValues = useSelector(widgetSelect.filterValues);

  if (!firstLoadComplete) return <div />; // TODO: GSFE-1689 Loading state / skeleton

  const marketTypeOptions = marketTypes.map(({ translationTag, id }) => ({
    name: formatMessage(marketTypeTranslationMap[translationTag as TranslationTag]),
    value: id
  }));

  const durationOptions = durations.map(({ translationTag, id, translationValue }) => ({
    name: formatMessage(durationTranslationMap[translationTag as TranslationTag], {
      0: translationValue
    }),
    value: id
  }));

  const selectedMarketType = marketTypeOptions.find(({ value }) => value === filterValues.market);
  const selectedDuration = durationOptions.find(({ value }) => value === filterValues.duration);

  if (!selectedMarketType || !selectedDuration) return null;

  return (
    <div className={styles.selectWrapper}>
      <Select
        initialSelectedItem={selectedDuration}
        items={durationOptions}
        label={formatMessage(TXTCLI_Duration)}
        disabled={filterDisabled}
        onChange={(selectItem) => onChangeHandler(selectItem, 'duration')}
      />
      <Select
        initialSelectedItem={selectedMarketType}
        items={marketTypeOptions}
        label={formatMessage(TXTWCL_Market)}
        disabled={filterDisabled}
        onChange={(selectItem) => onChangeHandler(selectItem, 'market')}
      />
    </div>
  );
};

export default Filters;
